import { ForwardedRef, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import axios, { AxiosProgressEvent } from 'axios';

import request from '../db/request';
import { GetPreSignedURLRequestConfig, PreSignedURLResponse } from '../db/types';
import { getFileNameComponents, getImageSizeFromUrl } from './files';
import Logger from './logger';

const log = Logger.create('UploadButton');

/**
 * This hook provides a function that returns whether the component is still mounted.
 * This is useful as a check before calling set state operations which will generates
 * a warning when it is called when the component is unmounted.
 * @returns a function
 *
 * @see https://stackoverflow.com/a/70716215/9709887
 */
export function useMounted(): () => boolean {
  const mountedRef = useRef(false);

  useEffect(function useMountedEffect() {
    mountedRef.current = true;
    return function useMountedEffectCleanup() {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(
    function isMounted() {
      return mountedRef.current;
    },
    [mountedRef]
  );
}

interface UseFileUploadProps {
  /**
   * Request payload to fetch S3's pre-signed upload url and
   * file's meta-data
   */
  requestConfig: GetPreSignedURLRequestConfig;
}

export const useFileUpload = ({ requestConfig }: UseFileUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState({ isIndeterminate: true, total: 0, uploaded: 0 });

  const getPreSignedURL = async (file: File) => {
    const { url, headers, data: payload } = requestConfig;
    const { name: originalFileName, extension: fileType } = getFileNameComponents(file.name);

    const { data } = await request.post<PreSignedURLResponse>(
      url,
      { fileType, originalFileName, ...payload },
      { headers }
    );

    return { ...data, originalName: originalFileName };
  };

  const handleUpload = async (
    file: File
  ): Promise<{ name: string; type: string; url: string; originalName: string } | { error: unknown }> => {
    try {
      setIsUploading(true);

      // get pre-signed url for s3 upload
      const { url, contentType, name, originalName, type } = await getPreSignedURL(file);

      // upload to s3
      await axios.put(url, file, {
        headers: {
          'Content-Type': contentType,
        },
        onUploadProgress: (e: AxiosProgressEvent) => {
          // if (!e.lengthComputable) return; // FIX: check updated value
          if (!e.total) return; // FIX: check updated value
          setProgress({ isIndeterminate: false, total: e.total, uploaded: e.loaded });
        },
      });

      setProgress({ isIndeterminate: true, total: 0, uploaded: 0 });
      return { name, type, url, originalName };
    } catch (error) {
      log.error(error);
      return { error };
    } finally {
      setIsUploading(false);
    }
  };

  return { isUploading, onUpload: handleUpload, progress };
};

export const useImage = (src?: string) => {
  const [fileSize, setFileSize] = useState(0);
  const [{ loaded, failed }, setState] = useState({ loaded: false, failed: false });

  useEffect(() => {
    if (!src) {
      return undefined;
    }

    setState({ loaded: false, failed: false });

    const abortController = new AbortController();

    // no operation callback
    const noOp = () => {};
    const onLoad = () => setState({ loaded: true, failed: false });
    const onError = () => setState({ loaded: false, failed: true });

    getImageSizeFromUrl(src, abortController.signal).then(setFileSize).catch(noOp);

    let image: HTMLImageElement | null = new Image();

    image.src = src;

    image.addEventListener('load', onLoad, { once: true });
    image.addEventListener('error', onError, { once: true });

    return () => {
      image?.removeEventListener('load', onLoad);
      image?.removeEventListener('error', onError);
      image = null;
      abortController.abort();
    };
  }, [src]);

  return { loaded, failed, fileSize };
};

export function useForwardedRef<T>(ref: ForwardedRef<T>) {
  const innerRef = useRef<T>(null);

  useEffect(() => {
    if (!ref) return;
    if (typeof ref === 'function') {
      ref(innerRef.current);
    } else {
      ref.current = innerRef.current;
    }
  });

  return innerRef;
}

export const useDimensions = (ref: RefObject<HTMLDivElement>) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    // Define resize callback function
    const updateDimensions = (entries: ResizeObserverEntry[]) => {
      if (!entries || entries.length === 0) return;

      const { width, height } = entries[0].contentRect;

      // Update state only if there's a meaningful change in width or height
      setDimensions((currentDimensions) => {
        if (
          Math.abs(currentDimensions.width - width) > 10 ||
          Math.abs(currentDimensions.height - height) > 10
        ) {
          return { width, height };
        }
        return currentDimensions; // Prevent re-render if dimensions are almost identical
      });
    };

    // Create observer with the updateDimensions callback
    resizeObserverRef.current = new ResizeObserver(updateDimensions);

    // Observe the target element only once
    if (ref.current) resizeObserverRef.current.observe(ref.current);

    // Cleanup on component unmount
    return () => {
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    };
  }, [ref]); // Ensure ref is stable, avoid re-creation of observer

  return dimensions;
};

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  element?: undefined,
  options?: boolean | AddEventListenerOptions
): void;

// Element Event based useEventListener interface
export function useEventListener<K extends keyof HTMLElementEventMap, T extends HTMLElement = HTMLDivElement>(
  eventName: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  element: RefObject<T>,
  options?: boolean | AddEventListenerOptions
): void;

// Document Event based useEventListener interface
export function useEventListener<K extends keyof DocumentEventMap>(
  eventName: K,
  handler: (event: DocumentEventMap[K]) => void,
  element: RefObject<Document>,
  options?: boolean | AddEventListenerOptions
): void;

export function useEventListener<
  KW extends keyof WindowEventMap,
  KH extends keyof HTMLElementEventMap,
  T extends HTMLElement | void = void,
>(
  eventName: KW | KH,
  handler: (event: WindowEventMap[KW] | HTMLElementEventMap[KH] | Event) => void,
  element?: RefObject<T>,
  options?: boolean | AddEventListenerOptions
) {
  // Create a ref that stores handler
  const savedHandler = useRef(handler);

  useLayoutEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // Define the listening target
    const targetElement: T | Window = element?.current || window;
    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    // Create event listener that calls handler function stored in ref
    const eventListener: typeof handler = (event) => savedHandler.current(event);

    targetElement.addEventListener(eventName, eventListener, options);

    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, options]);
}
